import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Download, Loader2, Terminal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useFetchImageUrl } from '@/lib/hooks/useFetchImageUrl';
import { useTaskExecutions } from '@/lib/api/task-execution';
import { createTask, TaskCommand, waitforTask } from '@/lib/api/task';
import { Action, ActionButton } from '@/pages/boxes-list/action-button';
import { DPad } from './d-pad';

// Command output (esp. logcat) can be huge; rendering it raw in a <pre> can
// freeze/crash the tab. Cap what we show inline — the full logcat is available
// via the Download button.
const MAX_OUTPUT_CHARS = 3000;
function clip(text: string) {
  if (text.length <= MAX_OUTPUT_CHARS) return text;
  return (
    text.slice(0, MAX_OUTPUT_CHARS) +
    `\n\n…[truncated ${text.length - MAX_OUTPUT_CHARS} more chars — use Download for the full output]`
  );
}

export function TaskExecution({
  ip,
  name,
  initialTaskId,
  actions,
  isTvControllerTask = false,
  locked = false,
}: {
  ip: string;
  name?: string;
  initialTaskId: string;
  /** When provided, renders an inline action menu that re-runs in place. */
  actions?: Action[];
  isTvControllerTask?: boolean;
  /** Box is in live view (scrcpy) — commands are disabled to avoid dropping it. */
  locked?: boolean;
}) {
  // Each box tracks its own task so it can be re-run in place without leaving
  // the page. Defaults to the task that opened this view.
  const [taskId, setTaskId] = useState(initialTaskId);
  // The id of an in-flight re-run the user triggered here. Only that drives the
  // spinner — viewing an existing task never spins.
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  const [executions] = useTaskExecutions(taskId);
  const execution = executions?.find((e) => e.ip === ip);
  const { data } = useFetchImageUrl(execution?.imageUri ?? '');
  const { data: logUrl } = useFetchImageUrl(execution?.logUri ?? '');

  const running = pendingTaskId !== null;

  // Safety net: stop the spinner as soon as the re-run's own result lands, even
  // if the backend never reports a terminal task status.
  useEffect(() => {
    if (pendingTaskId && taskId === pendingTaskId && execution) {
      setPendingTaskId(null);
    }
  }, [pendingTaskId, taskId, execution]);

  // Create a task for this box, switch the panel to it, and refresh in place.
  const runCommand = async (
    command: TaskCommand,
    label: string,
    commandData?: Record<string, unknown>
  ) => {
    let id: string;
    try {
      id = await createTask({
        boxes: [ip],
        command,
        isTvControllerTask,
        ...(commandData && { data: commandData }),
      });
    } catch (error) {
      console.error('createTask failed', error);
      toast.error(`Could not start ${label}`);
      return;
    }

    setPendingTaskId(id);
    setTaskId(id);

    try {
      await waitforTask(id);
      toast.success(`${label} completed`);
    } catch (error) {
      console.error('task failed', error);
      toast.error(`${label} failed`);
    } finally {
      setPendingTaskId(null);
    }
  };

  const onAction = (action: Action) =>
    runCommand(action.value as TaskCommand, action.label);

  const onKeyPress = (keycode: number, label: string, screenshot: boolean) => {
    if (screenshot) {
      // Select / Back / Home: capture and refresh the panel in place.
      runCommand(TaskCommand.KeyEvent, label, { keycode, screenshot: true });
      return;
    }
    // Arrow/navigation keys: just send the key — no capture, keep the current
    // screenshot, don't switch the panel's tracked task.
    createTask({
      boxes: [ip],
      command: TaskCommand.KeyEvent,
      isTvControllerTask,
      data: { keycode, screenshot: false },
    }).catch((error) => console.error('keyevent failed', error));
  };

  return (
    <Accordion type="single" collapsible defaultValue="item-1">
      <AccordionItem value="item-1">
        <div className="flex items-center justify-between gap-2 pr-2">
          <AccordionTrigger className="flex-1">
            <span className="flex items-center gap-2">
              {name ? (
                <>
                  {name}
                  <span className="text-muted-foreground">{ip}</span>
                </>
              ) : (
                ip
              )}
              {running && <Loader2 className="h-4 w-4 animate-spin" />}
            </span>
          </AccordionTrigger>
          {actions && actions.length > 0 && (
            <ActionButton
              disabled={running || locked}
              actions={actions}
              onActionSelected={onAction}
            />
          )}
        </div>
        <AccordionContent className="space-y-4">
          <div className="flex flex-wrap items-start gap-6">
            {data && (
              <div className="relative h-80 w-fit overflow-hidden rounded-lg bg-white sm:aspect-h-1 sm:aspect-w-2 lg:aspect-h-1 lg:aspect-w-1 group-hover:opacity-75 sm:h-64">
                <img
                  src={data}
                  alt={`Screen capture of ${name ?? ip}`}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            )}
            {actions && actions.length > 0 && (
              <DPad onPress={onKeyPress} disabled={running || locked} />
            )}
          </div>
          {logUrl && (
            <a
              href={logUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-accent"
            >
              <Download className="h-4 w-4" />
              Download logcat
            </a>
          )}
          {locked && (
            <p className="text-sm text-muted-foreground">
              Live view active — commands disabled to keep the stream connected.
            </p>
          )}
          {execution?.stdout && (
            <div className="flex gap-2 bg-primary-foreground p-4 rounded overflow-x-auto">
              <div>
                <Terminal className="block h-4 w-4" />
              </div>
              <pre className="mb-1 font-medium leading-none tracking-tight">
                {clip(execution.stdout)}
              </pre>
            </div>
          )}
          {execution?.stderr && (
            <div className="flex gap-2 bg-primary-foreground p-4 rounded overflow-x-auto">
              <div>
                <Terminal className="block h-4 w-4" />
              </div>
              <pre className="mb-1 font-medium leading-none tracking-tight">
                {clip(execution.stderr)}
              </pre>
            </div>
          )}
          {running && !execution && (
            <p className="text-sm text-muted-foreground">Running command…</p>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
